import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import { selectAccessToken } from 'services/session/sessionSelectors';
import { clearSession, refreshApToken } from 'services/session/sessionAsync';
import { selectBrandCode } from 'services/brand/brandSelectors';
import {
  API_REQUEST_METHODS,
  GENERAL,
  WITH_BEARER_TOKEN,
  WITH_OUT_BEARER_TOKEN,
} from 'utils/constants';
import { RootState, store } from 'app/store';
import api from './apiInstance';

const TRY_AGAIN_ERR_MSG = 'Something went wrong, please try again later.';

function includeBrandCode(
  headers: AxiosRequestConfig['headers'],
  brandCode: string,
) {
  if (headers) {
    headers['X-Idg-Brand'] = brandCode;
  }
}

const includeAuthorizationHeader = (
  configURL: string | undefined,
  headers: AxiosRequestConfig['headers'],
  accessToken: string | null,
) => {
  if (!configURL) return;
  WITH_BEARER_TOKEN.forEach((url) => {
    if (headers && configURL.includes(url)) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
  });
  WITH_OUT_BEARER_TOKEN.forEach((url) => {
    if (headers && configURL.includes(url)) {
      headers.Authorization = accessToken;
    }
  });
};

// Intercept all responses
let isRefreshing = false;
let failedQueue: any[] = [];
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 3;

// Function to process the queue
const processQueue = (error: unknown, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (
      error?.response?.status === 401 &&
      !originalRequest.retryAttempt &&
      !originalRequest.url.includes('/2fa') &&
      !originalRequest.url.endsWith('/refresh') &&
      !originalRequest.url.endsWith('/sessions')
    ) {
      originalRequest.retryAttempt = true;
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          await store.dispatch(refreshApToken());
          const appStore = store.getState() as RootState;
          const accessToken = selectAccessToken(appStore);
          const brandCode = selectBrandCode(appStore);
          includeBrandCode(originalRequest.headers, brandCode);
          includeAuthorizationHeader(
            originalRequest.url,
            originalRequest.headers,
            accessToken,
          );
          return api(originalRequest);
        } catch (refreshError) {
          refreshAttempts += 1;
          if (refreshAttempts <= MAX_REFRESH_ATTEMPTS) {
            processQueue(refreshError);
            return api(originalRequest);
          }
          store.dispatch(clearSession());
          throw refreshError;
          // Handle token refresh failure
          // Redirect to login page or handle error
        } finally {
          isRefreshing = false;
          refreshAttempts = 0;
        }
      } else {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            // Retry the original request
            return api(originalRequest);
          })
          .catch((err) => {
            throw err;
          });
      }
    }
    return Promise.reject(error);
  },
);

// End Intercept all responses

// Start - Interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error.message);
    }

    const axiosErr = error as AxiosError;

    // let errMsg = axiosErr?.message || GENERAL.defaultErrorMsg;
    let errMsg = GENERAL.defaultErrorMsg;

    if (axiosErr.response) {
      const { data } = axiosErr.response;
      const responseData = Array.isArray(data) ? data[0] : data;
      const resData = responseData as {
        errorMessage?: string;
        message?: string;
        code?: string;
        errorCode?: number;
      };

      const defaultErrMsg =
        resData?.errorMessage ||
        resData?.message ||
        resData?.code ||
        GENERAL.defaultErrorMsg;

      if (
        axiosErr.config?.url &&
        (axiosErr.config.url.includes('/2fa') ||
          axiosErr.config.url.endsWith('/sessions'))
      ) {
        return Promise.reject(axiosErr.response?.status);
      }

      switch (axiosErr.response?.status) {
        case 400:
          if (resData?.errorCode === 418) {
            errMsg = resData.errorCode.toString();
          } else {
            errMsg = defaultErrMsg;
          }
          break;
        case 404:
          if (resData?.message === 'HTTP 404 Not Found') {
            errMsg = TRY_AGAIN_ERR_MSG;
          } else {
            errMsg = defaultErrMsg;
          }
          break;
        case 500:
          if (resData?.message === 'Internal Server Error') {
            errMsg = TRY_AGAIN_ERR_MSG;
          } else {
            errMsg = defaultErrMsg;
          }
          break;
        case 504:
          if (resData?.message === 'Endpoint request timed out') {
            errMsg = TRY_AGAIN_ERR_MSG;
          } else {
            errMsg = defaultErrMsg;
          }
          break;
        default:
          errMsg = defaultErrMsg;
          break;
      }
    }
    // Handle response errors
    return Promise.reject(errMsg);
  },
);
// End - Interceptor to handle errors

/**
 * Common function to make API request
 */

async function makeAPIRequest(
  config: AxiosRequestConfig,
  method: string,
  accessToken: string | null,
  brandCode: string,
) {
  const headers = config?.headers ? { ...config?.headers } : {};
  includeBrandCode(headers, brandCode);
  includeAuthorizationHeader(config.url, headers, accessToken);

  try {
    const response = await api({ ...config, method, headers });
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
}

const getAPI = createAsyncThunk(
  API_REQUEST_METHODS.get.name,
  async (config: AxiosRequestConfig, thunkApi) => {
    const appStore = thunkApi.getState() as RootState;

    try {
      const accessToken = selectAccessToken(appStore);
      const brandCode = selectBrandCode(appStore);
      const response = await makeAPIRequest(
        config,
        API_REQUEST_METHODS.get.method,
        accessToken,
        brandCode,
      );
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

const postAPI = createAsyncThunk(
  API_REQUEST_METHODS.post.name,
  async (config: AxiosRequestConfig, thunkApi) => {
    const appStore = thunkApi.getState() as RootState;

    try {
      const accessToken = selectAccessToken(appStore);
      const brandCode = selectBrandCode(appStore);
      const response = await makeAPIRequest(
        config,
        API_REQUEST_METHODS.post.method,
        accessToken,
        brandCode,
      );
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

const putAPI = createAsyncThunk(
  API_REQUEST_METHODS.put.name,
  async (config: AxiosRequestConfig, thunkApi) => {
    const appStore = thunkApi.getState() as RootState;

    try {
      const accessToken = selectAccessToken(appStore);
      const brandCode = selectBrandCode(appStore);
      const response = await makeAPIRequest(
        config,
        API_REQUEST_METHODS.put.method,
        accessToken,
        brandCode,
      );
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

const patchAPI = createAsyncThunk(
  API_REQUEST_METHODS.patch.name,
  async (config: AxiosRequestConfig, thunkApi) => {
    const appStore = thunkApi.getState() as RootState;

    try {
      const accessToken = selectAccessToken(appStore);
      const brandCode = selectBrandCode(appStore);
      const response = await makeAPIRequest(
        config,
        API_REQUEST_METHODS.patch.method,
        accessToken,
        brandCode,
      );
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

const deleteAPI = createAsyncThunk(
  API_REQUEST_METHODS.delete.name,
  async (config: AxiosRequestConfig, thunkApi) => {
    const appStore = thunkApi.getState() as RootState;

    try {
      const accessToken = selectAccessToken(appStore);
      const brandCode = selectBrandCode(appStore);
      const response = await makeAPIRequest(
        config,
        API_REQUEST_METHODS.delete.method,
        accessToken,
        brandCode,
      );
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export { getAPI, postAPI, putAPI, patchAPI, deleteAPI };
